<template>
  <div class="tw-flex tw-flex-col tw-items-center tw-@container">
    <BillingCycleToggle
      v-model="currentBillingCycle"
      :billing-cycles="availableBillingCycles"
      :class="{ 'tw-hidden': disableBillingCycleToggle }"
      class="tw-mb-6"
    />

    <div class="tw-grid tw-w-full tw-max-w-6xl tw-gap-4 @3xl:tw-grid-cols-3">
      <!--Personal Subscription-->
      <div
        v-for="visiblePersonalPlan in visiblePersonalPlans"
        :key="visiblePersonalPlan.id"
        class="tw-relative tw-flex tw-flex-col tw-overflow-hidden tw-rounded-3xl tw-border tw-border-neutral-200 tw-bg-white tw-text-black tw-shadow-sm"
      >
        <div
          class="tw-bg-error-100 tw-py-2 tw-text-center tw-text-xs tw-font-semibold tw-uppercase tw-text-error-900"
          :class="{ 'tw-invisible': !visiblePersonalPlan.discounted }"
        >
          {{ coupon.isLimitedTimeOffer ? 'Limited time offer' : 'Discount' }}
          {{ visiblePersonalPlan.discounted?.discount * 100 }}%
        </div>

        <div class="tw-flex tw-grow tw-flex-col tw-px-8 tw-pb-8 tw-pt-4">
          <div class="tw-flex tw-justify-between">
            <span class="tw-text-xl tw-font-semibold tw-capitalize">
              {{ visiblePersonalPlan.group }}
            </span>
            <SubscriptionPlanBadge
              :group="visiblePersonalPlan.group"
              class="tw-size-12"
            />
          </div>

          <!--Discounted pricing-->
          <template v-if="visiblePersonalPlan.discounted">
            <div class="tw-flex tw-items-center tw-space-x-2">
              <div>
                <span class="tw-text-3xl tw-font-semibold">
                  {{
                    formatCurrency(
                      visiblePersonalPlan.discounted.amountPerMonth.total,
                    )
                  }}
                </span>
                <span class="tw-text-sm tw-text-neutral-400">/month</span>
              </div>
              <s class="tw-font-semibold tw-text-error-300">
                Was:
                {{ formatCurrency(visiblePersonalPlan.amountPerMonth.total) }}
              </s>
            </div>
            <div
              class="tw-mt-3 tw-flex tw-h-8 tw-items-center tw-text-xs tw-text-neutral-400"
            >
              {{ formatCurrency(visiblePersonalPlan.discounted.amount) }} billed
              {{ visiblePersonalPlan.humanBillingInterval }}
            </div>
          </template>
          <!--Normal pricing-->
          <template v-else>
            <div>
              <span class="tw-text-3xl tw-font-semibold">
                {{ formatCurrency(visiblePersonalPlan.amountPerMonth.total) }}
              </span>
              <span class="tw-text-sm tw-text-neutral-400">/month</span>
            </div>
            <div
              class="tw-mt-3 tw-flex tw-h-8 tw-items-center tw-text-xs tw-text-neutral-400"
            >
              {{ formatCurrency(visiblePersonalPlan.amount) }} billed
              {{ visiblePersonalPlan.humanBillingInterval }}
            </div>
          </template>

          <p class="tw-mt-6 tw-h-14">
            {{
              $page.props.content[
                visiblePersonalPlan.group === 'pro'
                  ? 'proPlanCardDescription'
                  : 'lightPlanCardDescription'
              ]
            }}
          </p>
          <div
            v-if="withSubscriptionBenefits"
            class="tw-mt-6 tw-grow tw-space-y-3 tw-text-sm"
          >
            <div>
              {{
                $page.props.content[
                  visiblePersonalPlan.group === 'pro'
                    ? 'proPlanPerks'
                    : 'lightPlanPerks'
                ]
              }}
            </div>
            <div
              v-for="perk in visiblePersonalPlan.perks"
              :key="perk.text"
              class="tw-flex tw-items-center"
            >
              <component
                :is="perk.icon"
                class="tw-mr-3 tw-h-5 tw-w-5 tw-shrink-0 tw-text-secondary-500"
              />
              <span class="tw-text-sm">{{ perk.text }}</span>
              <InfoTooltip
                v-if="perk.tooltip"
                :title="perk.text"
                :tooltip="perk.tooltip"
                placement="right"
                class="tw-ml-1"
              />
            </div>
          </div>
          <div
            v-tooltip="
              visiblePersonalPlan.group === 'light' &&
              isSubscriptionTrialInterval(SUBSCRIPTION_TRIAL_INTERVAL.MONTHLY)
                ? 'You are on a gifted light membership, so you can\'t buy the Light plan.'
                : ''
            "
            class="tw-mt-7"
          >
            <BaseButton
              @click="
                type === 'wizard'
                  ? startPersonalMembership(visiblePersonalPlan.id)
                  : handleGetStartedRedirectClick()
              "
              color="purple"
              is-block
              :is-disabled="
                isPurchasingPersonalPlan ||
                !canBuyPersonalSubscription(visiblePersonalPlan.group)
              "
              size="large"
            >
              {{
                isPurchasingPersonalPlan
                  ? 'Starting your membership...'
                  : 'Get Started'
              }}
            </BaseButton>
          </div>
        </div>
      </div>

      <!--Team Subscription-->
      <div
        v-if="visibleTeamPlan"
        class="tw-relative tw-flex tw-flex-col tw-overflow-hidden tw-rounded-3xl tw-bg-[#1D2939] tw-text-white tw-shadow-sm"
      >
        <div
          class="tw-bg-error-100 tw-py-2 tw-text-center tw-text-xs tw-font-semibold tw-uppercase tw-text-error-900"
          :class="{ 'tw-invisible': !visibleDiscountedTeamPlan }"
        >
          {{ coupon.isLimitedTimeOffer ? 'Limited time offer' : 'Discount' }}
          {{ visibleDiscountedTeamPlan?.discount * 100 }}%
        </div>

        <div class="tw-flex tw-grow tw-flex-col tw-px-8 tw-pb-8 tw-pt-4">
          <div class="tw-flex tw-justify-between">
            <span class="tw-text-xl tw-font-semibold tw-capitalize">Teams</span>
            <img
              src="@/assets/images/badge-plan-team.png"
              alt=""
              class="tw-h-12 tw-w-12"
            />
          </div>

          <template v-if="visibleDiscountedTeamPlan">
            <div class="tw-flex tw-items-center tw-space-x-2">
              <div>
                <!--                {{-->
                <!--                  formatCurrency(discountedInvoiceTotalWithoutTaxPerMonth)-->
                <!--                }}/mo-->
                <span class="tw-text-3xl tw-font-semibold">
                  {{ formatCurrency(discountedPerSeatRateMonth) }}
                </span>
                <span class="tw-text-sm tw-text-neutral-400">/month/user</span>
              </div>
              <s class="tw-font-semibold tw-text-error-300">
                Was:
                <!--                {{ formatCurrency(invoiceTotalWithoutTaxPerMonth) }}-->
                {{ formatCurrency(perSeatRateMonth) }}
              </s>
            </div>
          </template>
          <template v-else>
            <div>
              <!--              {{ formatCurrency(invoiceTotalWithoutTaxPerMonth) }}/mo-->
              <span class="tw-text-3xl tw-font-semibold">
                {{ formatCurrency(perSeatRateMonth) }}
              </span>
              <span class="tw-text-sm tw-text-neutral-400">/month/user</span>
            </div>
          </template>

          <div
            class="tw-mt-3 tw-flex tw-h-8 tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-2"
          >
            <select
              v-model="form.seats"
              class="tw-w-24 tw-shrink-0 tw-border-neutral-600 tw-bg-[#1D2939] tw-px-2 tw-py-1 tw-text-xs tw-text-white focus:tw-border-neutral-600 focus:tw-bg-[#1D2939] focus:tw-text-white focus:tw-outline-none focus:tw-ring-0"
            >
              <option
                v-for="seat in TEAM_SEATS_OPTIONS_VALUES"
                :key="seat"
                :value="seat"
              >
                {{ seat }} seats
              </option>
            </select>
            <!--            <div class="tw-text-xs tw-text-neutral-400">-->
            <!--              {{-->
            <!--                formatCurrency(-->
            <!--                  visibleDiscountedTeamPlan-->
            <!--                    ? discountedPerSeatRateMonth-->
            <!--                    : perSeatRateMonth,-->
            <!--                )-->
            <!--              }}/mo/user-->
            <!--            </div>-->
            <div class="tw-text-xs tw-text-success-500">
              (Save {{ percentageSavePerUserComparedToPersonalPlan }}%/user)
            </div>
          </div>
          <p class="tw-mt-6 tw-h-14">
            {{ $page.props.content.teamPlanCardDescription }}
          </p>
          <div
            v-if="withSubscriptionBenefits"
            class="tw-mt-6 tw-grow tw-space-y-3 tw-text-sm"
          >
            <div>
              {{ $page.props.content.teamPlanPerks }}
            </div>
            <div
              v-for="perk in teamPerks"
              :key="perk.text"
              class="tw-flex tw-items-center"
            >
              <component
                :is="perk.icon"
                class="tw-mr-3 tw-h-5 tw-w-5 tw-shrink-0"
              />
              <span>{{ perk.text }}</span>
              <InfoTooltip
                v-if="perk.tooltip"
                :title="perk.text"
                :tooltip="perk.tooltip"
                placement="right"
                class="tw-ml-1"
              />
            </div>
          </div>
          <BaseButton
            @click="
              type === 'wizard'
                ? createTeam(String(visibleTeamPlan.id), coupon.code)
                : redirectToRegisterPage()
            "
            color="white"
            is-block
            :is-disabled="isPurchasingTeamSubscription"
            size="large"
            class="tw-mt-7"
          >
            {{
              isPurchasingTeamSubscription
                ? 'Starting your membership...'
                : 'Get Started'
            }}
          </BaseButton>
        </div>
      </div>
    </div>

    <div v-if="withCouponCodeSection" class="tw-mt-6">
      <button
        class="tw--ml-1 tw-rounded-md tw-p-1 tw-text-sm tw-font-medium tw-text-neutral-500 focus:tw-outline-none focus-visible:tw-ring focus-visible:tw-ring-secondary-50"
        @click="toggleCouponCodeInput"
        test_id="have-a-coupon-btn"
      >
        {{ coupon.isInputShown ? 'Enter a discount code' : 'Have a coupon?' }}
      </button>
      <transition name="fade">
        <div v-if="coupon.isInputShown">
          <form
            @submit.prevent="applyCouponCode"
            class="tw-mt-2 tw-flex tw-items-center tw-space-x-2"
          >
            <FormInput
              v-model="couponCodeField"
              :is-error="Boolean(coupon.errors)"
              @input="clearCouponErrors"
              type="text"
              class="tw-grow"
              test_id="coupon-input"
            />
            <BaseButton
              color="outline-gray"
              :is-rounded="false"
              class="tw-h-[50px] tw-rounded-lg"
              :is-disabled="coupon.isLoading"
              test_id="apply-btn"
              type="submit"
            >
              Apply code
              <LoadingCircle
                v-if="coupon.isLoading"
                size="16"
                class="tw-ml-2"
              />
            </BaseButton>
          </form>
          <FormErrors
            v-if="Boolean(coupon.errors)"
            :errors="coupon.errors"
            class="tw-mt-2"
          />
          <p
            v-if="coupon.isValid && !coupon.isLimitedTimeOffer"
            class="tw-mt-2 tw-text-xs tw-text-neutral-500"
          >
            Your coupon code has been applied.
          </p>
        </div>
      </transition>
    </div>
  </div>
</template>

<script setup lang="ts">
import { router, usePage } from '@inertiajs/vue3'
import { computed, inject, ref } from 'vue'
import BillingCycleToggle from '@/modules/growthmentor/wizard/components/BillingCycleToggle.vue'
import { getHumanBillingInterval } from '@/shared/utils/subscriptionPlanInterval.js'
import { formatCurrency } from '@/shared/utils/formatCurrency.js'
import InfoTooltip from '@/shared/components/InfoTooltip.vue'
import { comparePlans, getPlanPerksWithIcons } from '@/shared/data/planPerks.js'
import BaseButton from '@/shared/components/BaseButton.vue'
import axios from 'axios'
import FormErrors from '@/shared/components/forms/FormErrors.vue'
import LoadingCircle from '@/shared/components/LoadingCircle.vue'
import FormInput from '@/shared/components/forms/FormInput.vue'
import { calculateTeamInvoiceTotal } from '@/shared/utils/calculateTeamInvoiceTotal.js'
import { useCreateTeam } from '@/modules/growthmentor/team/composables/useCreateTeam.ts'
import { CouponCodeContext } from '@/modules/growthmentor/wizard/types/coupon-code-context.ts'
import { notify } from '@kyvg/vue3-notification'
import { TEAM_SEATS_OPTIONS_VALUES } from '@/shared/data/teamSeatsOptions.ts'
import { usePermissionSystem } from '@/shared/composables/usePermissionSystem.js'
import SubscriptionPlanBadge from '@/shared/components/SubscriptionPlanBadge.vue'
import {
  ACCESS_TYPE,
  ROLE,
  SUBSCRIPTION_TRIAL_INTERVAL,
} from '@/shared/data/permissionSystem'

const props = withDefaults(
  defineProps<{
    withCouponCodeSection?: boolean
    withSubscriptionBenefits?: boolean
    disableBillingCycleToggle?: boolean
    type: 'wizard' | 'unlock-full-access-modal'
  }>(),
  {
    withCouponCodeSection: false,
    withSubscriptionBenefits: false,
    disableBillingCycleToggle: false,
  },
)

const page = usePage()
const { isSubscriptionGroup, isSubscriptionTrialInterval, hasRole, hasAccess } =
  usePermissionSystem()

const canBuyPersonalSubscription = (planGroup: 'light' | 'pro') => {
  // People on monthly trial can't buy light plan
  if (
    planGroup === 'light' &&
    isSubscriptionTrialInterval(SUBSCRIPTION_TRIAL_INTERVAL.MONTHLY)
  ) {
    return false
  }
  return !isSubscriptionGroup(planGroup.toUpperCase())
}

const currentBillingCycle = ref<'monthly' | 'quarterly' | 'annually'>(null)
const availableBillingCycles = computed(() => {
  const billingCycles = [
    ...personalPlans.value.map((plan) => plan.humanBillingInterval),
    ...teamPlans.value.map((plan) => plan.humanBillingInterval),
  ]
  return [...new Set(billingCycles)]
})

const handleGetStartedRedirectClick = () => {
  if (props.type !== 'unlock-full-access-modal') return

  if (hasRole(ROLE.GUEST)) {
    router.visit('/mentees/sign_up')
  } else if (hasAccess(ACCESS_TYPE.NONE)) {
    router.visit('/upgrade_plan/new')
  }
}

const {
  coupon,
  couponCodeField,
  applyCouponCode,
  toggleCouponCodeInput,
  clearCouponErrors,
  discountedPersonalPlans,
  discountedTeamPlans,
} = inject<CouponCodeContext>('coupon')

const isPurchasingPersonalPlan = ref(false)
const personalPlans = computed(() => {
  const plans = (
    props.type === 'wizard'
      ? page.props.plans
      : page.props.unlockFullAccessModalData.plans
  ) as {
    id: number
    name: string
    interval: 'year' | 'month'
    intervalCount: 1 | 3
    group: 'light' | 'pro'
    amount: number
    amountPerMonth: { subtotal: number; total: number; tax: number }
    discount?: number
    savingAmount?: number
  }[]
  return plans.map((plan) => ({
    ...plan,
    humanBillingInterval: getHumanBillingInterval(
      plan.interval,
      plan.intervalCount,
    ) as 'monthly' | 'quarterly' | 'annually',
  }))
})
const visiblePersonalPlans = computed(() => {
  return personalPlans.value
    .filter((plan) => plan.humanBillingInterval === currentBillingCycle.value)
    .map((plan) => ({
      ...plan,
      discounted: discountedPersonalPlans.value.find(
        (discountedPlan) => discountedPlan.id === plan.id,
      ),
      perks: getPlanPerksWithIcons(page.props.planPerks[plan.group]),
    }))
})
const startPersonalMembership = async (planId) => {
  try {
    isPurchasingPersonalPlan.value = true

    const { data } = await axios.post(
      page.props.openCheckoutSessionPath as string,
      {
        plan_id: planId,
        success_path: page.props.successPath,
        coupon_code: coupon.value.code,
      },
    )

    const stripe = Stripe(page.props.stripePublicKey)
    stripe.redirectToCheckout({ sessionId: data.checkout_session_stripe_id })
  } catch (error) {
    notify({
      type: 'error',
      text: 'An error occurred while starting your membership. Please try again.',
    })
  } finally {
    isPurchasingPersonalPlan.value = false
  }
}

// Teams

const {
  form,
  createTeam,
  isLoading: isPurchasingTeamSubscription,
} = useCreateTeam()
const teamPlans = computed(() => {
  const plans = (
    props.type === 'wizard'
      ? page.props.teamPlans
      : page.props.unlockFullAccessModalData.teamPlans
  ) as {
    id: number
    name: string
    interval: 'year' | 'month'
    intervalCount: 1 | 3
    amount: number
    amountInDollars: number
    mostPopular: boolean
    minimumSeats: number
    maximumSeats: number
    old: boolean
    tiers: { id: number; upTo: number; unitAmount: number; flatAmount: 0 }[]
  }[]
  return plans.map((plan) => ({
    ...plan,
    humanBillingInterval: getHumanBillingInterval(
      plan.interval,
      plan.intervalCount,
    ) as 'monthly' | 'quarterly' | 'annually',
  }))
})
const visibleTeamPlan = computed(() => {
  return teamPlans.value.find(
    (plan) => plan.humanBillingInterval === currentBillingCycle.value,
  )
})
const visibleDiscountedTeamPlan = computed(() => {
  return discountedTeamPlans.value.find(
    (plan) => plan.id === visibleTeamPlan.value.id,
  )
})
const teamPerks = computed(() => {
  return comparePlans(page.props.planPerks, 'team', 'pro')
})
const invoiceTotalWithoutTax = computed(() => {
  return calculateTeamInvoiceTotal(form.seats, visibleTeamPlan.value.tiers)
})
const discountedInvoiceTotalWithoutTax = computed(() => {
  return calculateTeamInvoiceTotal(
    form.seats,
    visibleDiscountedTeamPlan.value.tiers,
  )
})
const invoiceTotalWithoutTaxPerMonth = computed(() => {
  const divider = {
    monthly: 1,
    quarterly: 3,
    annually: 12,
  }

  return (
    invoiceTotalWithoutTax.value /
    divider[visibleTeamPlan.value.humanBillingInterval]
  )
})
const discountedInvoiceTotalWithoutTaxPerMonth = computed(() => {
  const divider = {
    monthly: 1,
    quarterly: 3,
    annually: 12,
  }

  return (
    discountedInvoiceTotalWithoutTax.value /
    divider[visibleTeamPlan.value.humanBillingInterval]
  )
})
const perSeatRateMonth = computed(() => {
  return invoiceTotalWithoutTaxPerMonth.value / form.seats
})
const discountedPerSeatRateMonth = computed(() => {
  return discountedInvoiceTotalWithoutTaxPerMonth.value / form.seats
})
const percentageSavePerUserComparedToPersonalPlan = computed(() => {
  const proPlan = visiblePersonalPlans.value.find(
    (plan) => plan.group === 'pro',
  )
  const personal = proPlan.discounted
    ? proPlan.discounted.amountPerMonth.total
    : proPlan.amountPerMonth.total
  const team = visibleDiscountedTeamPlan.value
    ? discountedPerSeatRateMonth.value
    : perSeatRateMonth.value
  return Math.round(((personal - team) / personal) * 100)
})
</script>
